import { FC, useMemo } from "react";
import clsx from "clsx";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import useTranslation

import { hexToRgba } from "utils/hexToRgba";

import { StyledTag } from "styles/components/tag";

type TagStatus = "approved" | "need-approval";

interface TagProps {
  label: string;
  className?: string;
  dataTestId?: string;
  bgColor?: string;
  color?: string;
  status?: TagStatus;
  alfa?: number;
}

const Tag: FC<TagProps> = ({
  label,
  status,
  bgColor,
  color,
  alfa,
  className,
  dataTestId,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(); // Initialize translation

  const tagClassName = clsx(["tag", className]);

  const statusTagData = useMemo(
    () => ({
      approved: {
        bgColor: theme.palette.blue.light,
        color: theme.palette.blue.main,
        label: t("approved"), 
      },
      "need-approval": {
        bgColor: theme.palette.yellow.light,
        color: theme.palette.yellow.main,
        label: t("need_approval"), 
      },
    }),
    [theme, t]
  );

  const tagColors = useMemo(() => {
    if (status) {
      return {
        bgColor: statusTagData[status].bgColor,
        color: statusTagData[status].color,
      };
    }

    if (alfa && bgColor) {
      return {
        bgColor: hexToRgba(bgColor, alfa),
        color,
      };
    }

    return {
      bgColor,
      color,
    };
  }, [alfa, bgColor, color, status, statusTagData]);

  const renderedMarker = useMemo(
    () =>
      status && (
        <span
          className="tag__marker"
          data-testid={dataTestId && `${dataTestId}-marker`}
        ></span>
      ),
    [dataTestId, status]
  );

  const renderedTagLabel = useMemo(
    () => (status ? statusTagData[status].label : label),
    [label, status, statusTagData]
  );

  return (
    <StyledTag
      color={tagColors.color}
      bgColor={tagColors.bgColor}
      markerColor={tagColors.color}
      className={tagClassName}
      data-testid={dataTestId}
    >
      {renderedMarker}
      <span
        className="tag__label"
        data-testid={dataTestId && `${dataTestId}-label`}
      >
        {renderedTagLabel}
      </span>
    </StyledTag>
  );
};

export default Tag;
